/**
 * 서버와 브라우저가 함께 쓰는 상수.
 *
 * `lib/site.ts` 는 `next/headers` 를 쓰는 **서버 전용**이라 클라이언트 컴포넌트가
 * 가져올 수 없다. 양쪽이 같은 값을 봐야 하는 것만 여기 둔다.
 */

/**
 * 로그인 화면이 "동시접속으로 끊겼다" 를 알아보는 주소 표시.
 * 로그인이 필요한 화면의 관문(`requireMe`)이 붙이고, 셸의 알림창(`AlertHost`)이 읽는다.
 */
export const EVICTED_PARAM = 'evicted';

/** 로그인 토큰 쿠키 이름 — BFF 로그인 라우트가 심고, 관문(`proxy.ts`)이 있는지만 본다 */
export const TOKEN_COOKIE = 'sp_token';

/**
 * PC Shell 앱이 물고 들어오는 로그인 쿠키(2026-09-29 — 현행 `Player.php` 의 이름 그대로).
 *
 * 앱은 제 브라우저 창에 이 쿠키만 심고 우리 재생 페이지를 연다. 그러니 **로그인 쿠키를 읽는 자리마다
 * 이 이름도 함께 본다** — 없으면 셸 안에서 "로그인이 필요합니다" 로 튕긴다. 값은 우리가 발급한
 * 로그인 토큰(JWT)이라 `sp_token` 과 똑같이 다루면 된다.
 */
export const SHELL_COOKIE = 'STAR_SESSION';

/**
 * 로그인 토큰을 담은 쿠키 — 브라우저 것이 먼저, 없으면 Shell 앱이 심은 것.
 *
 * **`const` 화살표가 아니라 `function` 이다**(2026-09-29). 화살표로 두었더니 dev 에서
 * `loginCookieValue is not a function` 이 나며 BFF(`/api/…`) 전체가 500 이 됐다 — 이 파일은
 * 미들웨어(Edge)와 라우트(Node)가 함께 가져가는데, 그 경계에서 `const` 내보내기가 아직 채워지기
 * 전에 불린다. 같은 파일의 `needsLogin` 은 `function` 이라 멀쩡했다.
 */
export function loginCookieValue(get: (name: string) => string | undefined): string | undefined {
  return get(TOKEN_COOKIE) ?? get(SHELL_COOKIE);
}
/**
 * 로그인이 필요한 화면들 — `requireMe()` 가 지키는 경로와 **같은 목록**이어야 한다.
 * 세션이 끊긴 알림에서 [취소]를 눌렀을 때 홈으로 보낼지 정하고, **관문(`proxy.ts`)이 막을 자리**도 이 목록이다.
 */
const LOGIN_REQUIRED = ['/my', '/cart', '/checkout', '/subscribe/checkout', '/orders/complete'];
/**
 * 경로 앞머리로는 가릴 수 없는 것들 — 글쓰기·글수정, 그리고 재생(맛보기 `/player/free/…` 는 로그인 없이 본다).
 *
 * **`/player/js/…` 도 빼야 한다**(2026-09-21 발견) — 그 아래는 화면이 아니라 **벤더 번들 파일**이다
 * (`public/player/js/…`, 자산 동기화가 채운다). 막아 두면 비로그인 맛보기가 번들을 못 받아
 * 로그인 페이지의 HTML 을 스크립트로 읽고 `StarPlyr not loaded` 로 죽는다.
 */
const LOGIN_REQUIRED_RE = [/^\/boards\/[^/]+\/(write|[^/]+\/edit)$/, /^\/player\/(?!free\/|js\/)/];

/** 지금 보고 있는 곳이 로그인해야 볼 수 있는 화면인가 */
export function needsLogin(pathname: string): boolean {
  return LOGIN_REQUIRED.some((p) => pathname === p || pathname.startsWith(`${p}/`))
    || LOGIN_REQUIRED_RE.some((re) => re.test(pathname));
}

/**
 * 레프트바 한 줄 — 설정>기능옵션에서 짠 좌측 고정 메뉴(현행 `config.leftbar`).
 * **주소는 서버가 이미 풀어 준다**(게시판·프로모션은 번호로 저장돼 화면이 알 수 없다).
 * `href` 가 null 이면 갈 곳이 사라진 항목이라 누를 수 없게 그린다.
 */
export interface LeftbarLink {
  name: string;
  href: string | null;
  blank: boolean;
  children: LeftbarLink[];
}
